/**
 * Inline 7-day remaining-ratio sparkline for the VPS nodes table.
 * Hand-rolled SVG polyline — same house pattern as the TrafficMonitor
 * trend panel (TrafficMonitorPage.tsx TrendPanel), zero chart deps,
 * Tailwind stroke/fill utilities only (design guardrail: no hex).
 * Pure component: all copy arrives via props, so tests render it bare.
 */
const WIDTH = 140;
const HEIGHT = 36;
const PAD = 3;

export interface VpsTrendSamplePoint {
  readonly sampledAt: string;
  readonly remainingRatio: number;
}

export interface VpsDailyBurn {
  readonly day: string;
  readonly burnedRatio: number;
}

const TONE_STROKES = {
  ok: 'stroke-signal',
  warn: 'stroke-warn',
  bad: 'stroke-bad',
} as const;

type Tone = keyof typeof TONE_STROKES;

export function clampRatio(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function toneOf(latestRatio: number): Tone {
  if (latestRatio < 0.1) return 'bad';
  if (latestRatio < 0.2) return 'warn';
  return 'ok';
}

/**
 * Groups consecutive-sample remaining_ratio drops by the UTC day of the
 * later sample. `max(0, ·)` clamps monthly counter resets (upward jumps)
 * to zero — same semantics as the server-side burn clamp (spec §3).
 */
export function computeDailyBurnRatios(
  samples: readonly VpsTrendSamplePoint[],
): readonly VpsDailyBurn[] {
  const byDay = new Map<string, number>();
  for (let index = 1; index < samples.length; index += 1) {
    const prev = samples[index - 1];
    const next = samples[index];
    if (prev === undefined || next === undefined) continue;
    const delta = Math.max(0, clampRatio(prev.remainingRatio) - clampRatio(next.remainingRatio));
    const day = next.sampledAt.slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + delta);
  }
  return Array.from(byDay.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([day, burnedRatio]) => ({ day, burnedRatio }));
}

interface VpsTrendSparklineProps {
  readonly samples: readonly VpsTrendSamplePoint[];
  readonly testId: string;
  readonly label: string;
  readonly emptyLabel: string;
}

export function VpsTrendSparkline({
  samples,
  testId,
  label,
  emptyLabel,
}: VpsTrendSparklineProps): JSX.Element {
  if (samples.length < 2) {
    return (
      <span className="font-mono text-micro text-paper-low" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </span>
    );
  }
  const latest = samples[samples.length - 1];
  const stroke = TONE_STROKES[toneOf(clampRatio(latest?.remainingRatio ?? 0))];
  const step = (WIDTH - PAD * 2) / (samples.length - 1);
  const points = samples
    .map((sample, index) => {
      const x = PAD + step * index;
      const y = HEIGHT - PAD - clampRatio(sample.remainingRatio) * (HEIGHT - PAD * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      role="img"
      aria-label={label}
      data-testid={testId}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="block h-9 w-[140px] rounded bg-well"
    >
      <line x1={PAD} x2={WIDTH - PAD} y1={HEIGHT - PAD} y2={HEIGHT - PAD} strokeWidth={1} className="stroke-line" />
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={stroke}
      />
    </svg>
  );
}
